import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
  useId,
} from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Button from "./Button";
import cross from "../../assets/icons/cross.svg";
import { COUNTRIES_JSON_URL } from "@/constants/assets";

const CountryCodeDropdown = ({
  value,            // selected country_code like "+44"
  onChange,         // (country_code) => void
  className = "",
  area,
  parentToggle,     // open/close controlled by parent (boolean)
}) => {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [activeIndex, setActiveIndex] = useState(0);
  const [countryList, setCountryList] = useState([]);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  const inputId = useId();
  const listboxId = useId();


  // Fetch countries.json without any manual cache
  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        const res = await fetch(COUNTRIES_JSON_URL, {
          signal: ctrl.signal,
          cache: "no-store", // no cache
        });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const json = await res.json();
        if (!mounted) return;

        const clean = Array.isArray(json) ? json.filter(isCountry) : [];
        setCountryList(clean);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("countries.json fetch error:", err);
        }
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  // Derive selected from value (read-only)
  const selected = useMemo(
    () => countryList.find((c) => c?.country_code === value) || null,
    [countryList, value]
  );

  // Filter + rank results (supports digits-only query like "91" -> "+91")
  const filteredData = useMemo(() => {
    const q = normalize(deferredQuery);
    const qDigits = digits(deferredQuery);

    if (!q && !qDigits) return countryList;

    const matches = countryList.filter((item) => {
      const name = normalize(item.country_name);
      const code = normalize(item.country_code);
      const digitCode = digits(item.country_code);
      return (
        name.includes(q) ||
        code.includes(q) ||
        (!!qDigits && digitCode.startsWith(qDigits))
      );
    });

    return matches
      .map((item) => ({
        item,
        score: Math.max(
          rankMatch(item, q),
          digits(item.country_code).startsWith(qDigits) && qDigits ? 2 : -1
        ),
      }))
      .sort(
        (a, b) => b.score - a.score || a.item.country_name.localeCompare(b.item.country_name)
      )
      .map((x) => x.item);
  }, [countryList, deferredQuery]);

  // Keep activeIndex valid & ensure it's visible
  useEffect(() => {
    if (activeIndex >= filteredData.length) {
      setActiveIndex(0);
      return;
    }
    const el = itemRefs.current[activeIndex];
    const list = listRef.current;
    if (el && list) {
      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const viewTop = list.scrollTop;
      const viewBottom = viewTop + list.clientHeight;
      if (elTop < viewTop) list.scrollTop = elTop;
      else if (elBottom > viewBottom) list.scrollTop = elBottom - list.clientHeight;
    }
  }, [filteredData, activeIndex, parentToggle]);

  const onPick = useCallback(
    (item) => {
      onChange?.(item?.country_code);
      setQuery("");
      inputRef.current?.focus();
    },
    [onChange]
  );

  const onKeyDown = (e) => {
    if (!parentToggle || !filteredData.length) return;
    const len = filteredData.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % len);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + len) % len);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(len - 1);
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 5, len - 1));
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 5, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredData[activeIndex]) onPick(filteredData[activeIndex]);
    } else if (e.key === "Escape") {
      inputRef.current?.blur();
    }
  };

  const flagUrl = selected?.flag_url || "https://flagcdn.com/w320/gb.png";
  const countryCode = selected?.country_code || "+44";

  return (
    <div className="relative dropdown flex items-center md:h-10" ref={menuRef}>
      <Button
        className={`focus:outline-none font-medium rounded-lg text-[11px] sm:text-[13px] inline-flex ${className}`}
        onClick={() => setQuery("")}
        aria-haspopup="listbox"
        aria-expanded={!!parentToggle}
        aria-controls={listboxId}
        aria-labelledby={inputId}
        role="combobox"
      >
        <div className="flex items-center">
          <img
            src={flagUrl}
            className="w-[2rem] h-[1.5rem] object-cover rounded"
            loading="lazy"
            alt=""
          />
          <span
            className={`ml-1 sm:ml-2 text-xs sm:text-sm font-inter ${
              area !== "Login" ? "w-20" : "w-14"
            }`}
          >
            {countryCode}
          </span>
          <span className="ml-1 xs:ml-0 sm:ml-0 flex-col items-end">
            <ChevronDownIcon className="w-3 h-3" />
          </span>
        </div>
      </Button>

      {parentToggle && (
        <div
          className={`absolute left-0 z-50 divide-y divide-gray-100 rounded-lg shadow bg-gray-50 max-h-[238px] overflow-auto ${
            area !== "Login" ? "-ml-3 mt-7 sm:mt-8" : "-ml-[18px] mt-[200px] sm:mt-[183px]"
          } w-[260px] xs:w-[312px] sm:w-[300px]`}
          role="listbox"
          id={listboxId}
          aria-activedescendant={filteredData[activeIndex]?.country_code || ""}
          onKeyDown={onKeyDown}
          ref={listRef}
        >
          <div
            className="flex justify-between px-2 py-3 border-b"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-inter text-xs text-loyalityDate w-[90%]">
              <input
                id={inputId}
                ref={inputRef}
                type="text"
                placeholder="Search"
                className="outline-none text-xs w-full bg-gray-50"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="w-[10%] flex justify-end">
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="p-1"
                >
                  <img src={cross} className="w-4 h-4 md:h-5 md:w-5" alt="" />
                </button>
              ) : null}
            </div>
          </div>

          <ul className="text-[11px] sm:text-[13px] text-gray-700">
            {filteredData.map((val, idx) => (
              <li
                key={`${val?.country_code}-${val?.country_name}`}
                id={val?.country_code}
                ref={(el) => (itemRefs.current[idx] = el)}
              >
                <Button
                  onClick={() => onPick(val)}
                  className={`block px-2 py-3 w-full border-b hover:bg-gray-100 focus:bg-gray-100 ${
                    idx === activeIndex ? "bg-gray-100" : ""
                  }`}
                  role="option"
                  aria-selected={value === val?.country_code}
                >
                  <div className="flex items-center w-full">
                    <div className="w-[28%] flex items-center">
                      <img
                        src={val?.flag_url}
                        className="w-6 h-4 object-cover rounded"
                        loading="lazy"
                        alt=""
                      />
                      <span className="text-sm sm:text-md font-inter font-medium ml-2">
                        {val?.country_code}
                      </span>
                    </div>
                    <div className="w-[72%] flex items-center ml-2">
                      <span className="text-md font-inter text-disabled">
                        {val?.country_name}
                      </span>
                    </div>
                  </div>
                </Button>
              </li>
            ))}
            {!filteredData.length && (
              <li className="px-3 py-4 text-xs text-gray-500">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryCodeDropdown;

const normalize = (s) => (s || "").toLowerCase();
const digits = (s) => (s || "").replace(/\D+/g, "");
const isCountry = (x) =>
  x &&
  typeof x === "object" &&
  typeof x.country_name === "string" &&
  typeof x.country_code === "string" &&
  typeof x.flag_url === "string";

const rankMatch = (item, q) => {
  if (!q) return 0;
  const name = normalize(item.country_name);
  const code = normalize(item.country_code);
  if (name.startsWith(q) || code.startsWith(q)) return 2;
  if (name.includes(q) || code.includes(q)) return 1;
  return -1;
};
